// Third-party
import {
  Palette,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

// Internal
import { ExpandableDialog } from "@components/ExpandableDialog";

// Types
import { IChatBlock } from "@pages/askDost/types";

interface ITableBlockProps {
  block: IChatBlock;
  palette: Palette;
  translate: (text: string, options?: any) => string;
}

export const TableBlock: React.FC<ITableBlockProps> = ({
  block,
  palette,
  translate,
}) => {
  const { columns, rows, title, toolStatus } = block;
  const isPending = toolStatus === "pending" || toolStatus === "in_progress";

  if (!isPending && (!columns || !rows)) return null;

  return (
    <ExpandableDialog title={title || ""} translate={translate}>
      <TableWrapper
        columns={columns || []}
        rows={rows || []}
        palette={palette}
        isPending={isPending}
      />
    </ExpandableDialog>
  );
};

const TableWrapper = ({
  isExpanded,
  columns,
  rows,
  palette,
  isPending,
}: any) => (
  <div style={{ overflowX: "auto" }}>
    <MyTable
      columns={columns}
      rows={rows}
      palette={palette}
      isExpanded={isExpanded}
      isPending={isPending}
    />
  </div>
);

const MyTable = ({ isExpanded, columns, rows, palette, isPending }) => {
  if (isPending) {
    const columnCount = columns?.length || 3;
    return (
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor:
                palette.mode === "dark"
                  ? palette.grey[800]
                  : palette.text.input,
            }}
          >
            {Array.from({ length: columnCount }).map((_, colIdx) => (
              <TableCell key={colIdx} sx={{ fontWeight: 600 }}>
                <Skeleton variant="text" width="80%" />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 5 }).map((_, rowIdx) => (
            <TableRow key={rowIdx}>
              {Array.from({ length: columnCount }).map((_, cellIdx) => (
                <TableCell key={`${rowIdx}-${cellIdx}`}>
                  <Skeleton variant="text" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow
          sx={{
            backgroundColor:
              palette.mode === "dark" ? palette.grey[800] : palette.text.input,
          }}
        >
          {columns?.map((col: string) => (
            <TableCell key={col} sx={{ fontWeight: 600 }}>
              {col}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows?.map((row: any[], idx: number) => (
          <TableRow
            key={idx}
            sx={{
              borderBottom: isExpanded ? undefined : 0,
              borderRight: isExpanded ? undefined : 0,
              borderLeft: isExpanded ? undefined : 0,
            }}
          >
            {row?.map((cell, cellIdx) => (
              <TableCell key={`${idx}-${cellIdx}`}>{cell ?? "-"}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
